import queryString from 'query-string';
import get from 'lodash/get';

import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import {
  CATALOG_GENRE_DETAIL_SET_DATA,
  CATALOG_GENRE_DETAIL_RESET_DATA,
} from 'rebook/apps/catalog/constants';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import { Genre } from 'rebook/apps/catalog/models';
import getFloorNumber from 'rebook/apps/utils/text/getFloorNumber';
import getPageNumberString from 'rebook/apps/utils/text/getPageNumberString';


export function fetchInitialData(match, location = {}, paginateBy) {
  return async (dispatch, getState) => {
    const { ssr, genreDetail: currentState } = getState();
    const pageNumber = parseInt(get(ssr, 'query.page', 1), 10);
    const orderBy = get(ssr, 'query.o', 'popular');
    const subscriptionId = get(ssr, 'query.s', null);
    const bookType = get(ssr, 'query.t', null);
    let newState = {
      match,
      paginateBy,
      pageNumber,
      orderBy,
      subscriptionId,
      bookType,
    };
    // obtain partial state from router (client side only)
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.genre) {
      newState = {
        ...newState,
        ...currentState,
      };
    }
    dispatch(setData(newState));
    // partial genre state may be available from router
    // so we have to fetch the full object detail
    if (get(newState, 'genre.niche', null) && !newState.similarGenres) {
      await dispatch(fetchSimilarGenres(newState.genre.niche.resource_uri));
    }
    if (!newState.genre) {
      await dispatch(fetchDetail(match));
    }
    await dispatch(fetchBookList(newState, pageNumber));
  };
}

export function fetchDetail(match) {
  return async (dispatch, getState) => {
    const { ssr } = getState();
    const { genreSlug } = match.params;
    const url = `/api/catalog/genres/s-${genreSlug}/`;

    return (
      fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
        .then(
          ({ data }) => {
            dispatch(setData({
              genre: data,
            }));
            return data;
          },
        ).then((data) => {
          dispatch(fetchSimilarGenres(data.niche.resource_uri));
          return data;
        })
        .catch(
          ({ data, resp, error }) => {
            if (resp.status === 404) {
              dispatch(Error404Actions.show());
            }
            else {
              Raven.captureMessage('failed to load genre detail due to server error', {
                level: 'error',
                extra: {
                  status: resp.status,
                  response: data,
                  error,
                },
              });
              throw new Error('failed to load genre detail due to server error');
            }
          },
        )
    );
  };
}

export function fetchSimilarGenres(nicheResourceUri) {
  return async (dispatch, getState) => {
    const { ssr } = getState();
    fetchQuery(nicheResourceUri, { headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({ similarGenres: data.genres }));
      });
  };
}

export function fetchBookList(params = {}) {
  return async (dispatch, getState) => {
    const { ssr, genreDetail } = getState();
    const mergedState = Object.assign({}, genreDetail, params);
    const { genre, paginateBy, orderBy, subscriptionId, bookType } = mergedState;
    let pageNumber = mergedState.pageNumber;
    const catalogBookType = get(mergedState.match, 'params.bookType', '');
    const areBooksAudio = catalogBookType === 'audio';
    if (!params.pageNumber &&
      (genreDetail.subscriptionId !== mergedState.subscriptionId)
      || (genreDetail.bookType !== mergedState.bookType)
    ) {
      pageNumber = 1;
    }
    if (!genre) {
      return null;
    }

    let queryParams = {
      genres: genre.id,
      offset: (pageNumber - 1) * paginateBy,
      limit: paginateBy,
      o: orderBy,
      author_weight__gte: 0,
      ...(subscriptionId !== null && { subscription_id__in: subscriptionId }),
      ...(bookType !== null && { type: bookType }),
    };

    if (bookType === 'audio' || areBooksAudio) {
      queryParams['type'] = 'audio';
    }

    const qs = queryString.stringify(queryParams);
    const url = `/api/books/?${qs}`;

    const pendingState = {
      // clear the book list before the fetch
      books: null,
      // optimistically update the UI
      pageNumber: pageNumber,
      orderBy,
      lastPageNumber: 0,
      ...(subscriptionId !== null && { subscriptionId }),
      ...(bookType !== null && { bookType }),
    };
    dispatch(setData(pendingState));
    return (
      fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
        .then(
          ({ data }) => {
            const lastPageNumber = Math.ceil(data.meta.total_count / paginateBy);
            dispatch(setData({
              books: data.objects,
              lastPageNumber: lastPageNumber,
              totalBookCount: data.meta.total_count,
              subscriptionId,
              bookType,
            }));
          },
        )
        .catch(
          ({ data, resp, error }) => {
            if (resp.status === 404) {
              dispatch(Error404Actions.show());
            }
            Raven.captureMessage('failed to load genre books due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load genre books due to server error');
          },
        )
    );
  };
}

export function setSeoMeta() {
  return (dispatch, getState) => {
    const { payments, genreDetail } = getState();
    const isAudio = genreDetail.bookType === 'audio' || get(genreDetail, 'match.params.bookType', null) === 'audio';
    const pageNumber = genreDetail.pageNumber;
    const genre = new Genre(genreDetail.genre);
    const catalogBookType = get(genreDetail.match, 'params.bookType', '');
    const areBooksAudio = catalogBookType === 'audio';

    const genreNicheSlug = genre.niche ? genre.niche.slug : genre.absolute_url;
    const subscriptionPrice = ['biznes-knigi'].indexOf(genreNicheSlug) >= 0
      ? payments.prices.audio['1'].full_price
      : payments.prices.standard['1'].full_price;
    const title = isAudio
      ? `«${genre.name}» — слушать аудиокниги онлайн на MyBook.`
      : `Книги в жанре «${genre.name}» (${genre.active_book_count}) - ` +
      `последние поступления и популярные книги в электронной библиотеке MyBook.` +
      `${getPageNumberString(pageNumber)}`.trim();

    const description = isAudio
      ? `Аудиокниги в жанре «${genre.name}» — слушайте новинки и бестселлеры жанра, ` +
      `популярных авторов и чтецов, делитесь рецензиями в электронной библиотеке MyBook.`
      : `Книги в жанре «${genre.name}». ` +
      `Новинки и бестселлеры жанра ${genre.name}, популярные авторы и лучшие рецензии ` +
      `в электронной библиотеке MyBook. ${getPageNumberString(pageNumber)}`.trim();

    const canonicalUrl = areBooksAudio
      ? `https://${BASE_HOST}${genre.getGenreUrl()}books/audio/`
      : `https://${BASE_HOST}${genre.getGenreUrl()}`;

    const defaultOgDescription = `${getFloorNumber(genre.active_book_count)} ` +
      `${genre.seo_head ? genre.seo_head : genre.name} ` +
      `всего за ${subscriptionPrice} рублей в месяц. ` +
      `Читать онлайн, на iPhone, iPad или Android бестселлеры ` +
      `${genre.seo_tail ? genre.seo_tail : genre.name}.`;
    const ogDescription = genre.open_graph ? genre.open_graph : defaultOgDescription;
    const ogTags = [
      { name: 'description', value: ogDescription },
    ];
    const ogTwitterTags = [
      { name: 'description', value: ogDescription },
    ];
    dispatch(SeoActions.setData({
      title,
      description,
      ogTags,
      ogTwitterTags,
      canonicalUrl,
      pageNumber,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { genre } = payload;
    dispatch({
      type: CATALOG_GENRE_DETAIL_SET_DATA,
      payload,
    });
    if (genre) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch({
      type: CATALOG_GENRE_DETAIL_RESET_DATA,
    });
    dispatch(SeoActions.resetData());
  };
}
